import { ChevronRight, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFolderAncestors } from '../../hooks/useFolderTree';
import type { Folder } from '../../types/api';

interface FolderBreadcrumbsProps {
	/** Current folder ID */
	currentFolderId?: string;
	/** Current folder object (if available) */
	currentFolder?: Folder;
	/** Base path for folder links */
	basePath?: string;
	/** Whether to show the home/root link */
	showHome?: boolean;
	/** Custom CSS classes */
	className?: string;
	/** Custom separator component */
	separator?: React.ReactNode;
}

/**
 * FolderBreadcrumbs component displays the folder hierarchy path
 */
export function FolderBreadcrumbs({
	currentFolderId,
	currentFolder,
	basePath = '/folders',
	showHome = true,
	className = '',
	separator,
}: FolderBreadcrumbsProps) {
	const { data: ancestors, isLoading } = useFolderAncestors(currentFolderId || '');

	// Default separator
	const defaultSeparator = (
		<ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 mx-1" />
	);
	const separatorElement = separator || defaultSeparator;

	// Show loading state
	if (isLoading && currentFolderId) {
		return (
			<nav className={`flex items-center text-sm ${className}`} aria-label="Breadcrumb">
				<div className="flex items-center space-x-2 animate-pulse">
					<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
					{separatorElement}
					<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
					{separatorElement}
					<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
				</div>
			</nav>
		);
	}

	// Build breadcrumb items
	const breadcrumbItems = [];

	// Add home/root if enabled
	if (showHome) {
		breadcrumbItems.push({
			id: 'root',
			name: 'Root',
			href: basePath,
			isHome: true,
		});
	}

	// Add ancestors
	if (ancestors && ancestors.length > 0) {
		ancestors.forEach(ancestor => {
			breadcrumbItems.push({
				id: ancestor.id,
				name: ancestor.name,
				href: `${basePath}/${ancestor.id}`,
				folder: ancestor,
			});
		});
	}

	// Add current folder if provided and not already in ancestors
	if (currentFolder && !breadcrumbItems.some(item => item.id === currentFolder.id)) {
		breadcrumbItems.push({
			id: currentFolder.id,
			name: currentFolder.name,
			href: `${basePath}/${currentFolder.id}`,
			folder: currentFolder,
			isCurrent: true,
		});
	}

	// If no breadcrumbs and no current folder, show just root
	if (breadcrumbItems.length === 0 && !currentFolderId) {
		return (
			<nav className={`flex items-center text-sm ${className}`} aria-label="Breadcrumb">
				<Link
					to={basePath}
					className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
				>
					<Home className="w-4 h-4 mr-1" />
					Root
				</Link>
			</nav>
		);
	}

	return (
		<nav className={`flex items-center text-sm ${className}`} aria-label="Breadcrumb">
			<ol className="flex items-center space-x-1">
				{breadcrumbItems.map((item, index) => (
					<li key={item.id} className="flex items-center">
						{/* Add separator before non-first items */}
						{index > 0 && separatorElement}

						{/* Breadcrumb item */}
						{item.isCurrent ? (
							<span
								className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs"
								aria-current="page"
								title={item.name}
							>
								{item.isHome && <Home className="w-4 h-4 mr-1 inline" />}
								{item.name}
							</span>
						) : (
							<Link
								to={item.href}
								className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors truncate max-w-xs flex items-center"
								title={item.name}
							>
								{item.isHome && <Home className="w-4 h-4 mr-1" />}
								{item.name}
							</Link>
						)}
					</li>
				))}
			</ol>
		</nav>
	);
}

export default FolderBreadcrumbs;
